import { useModal } from '@client/components/common/Modal';
import {
  faClone,
  faLink,
  faPen,
  faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isSameYear } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useState } from 'react';
import { EditForm } from './EditFormModal';
import { AugmentedClass } from './types';
import { clsToFormData, formDataToMutationData } from './utils';
import {
  useAddLiveClassMutation,
  useUpdateLiveClassMutation,
} from '../../../../generated/graphql';

const CopyDeepLink = ({ cls }: { cls: AugmentedClass }) => {
  const [copied, setIsCopied] = useState(false);
  return (
    <div
      className="relative cursor-pointer"
      onMouseLeave={() => {
        setIsCopied(false);
      }}
      onClick={() => {
        navigator.clipboard.writeText(cls.deepLink);
        setIsCopied(true);
      }}
    >
      <div
        className={`inline-block min-w-max absolute ${
          copied ? '' : 'invisible'
        } z-10 bottom-5 right-0 py-2 px-3 text-sm bg-white text-black rounded-lg shadow-md `}
      >
        <span>Copied to clipboard</span>
        <FontAwesomeIcon
          icon={faCheck as any}
          className="ml-2 text-green-500 text-md leading-none"
        />
      </div>
      <FontAwesomeIcon
        icon={faLink as any}
        className="text-primary text-md leading-none cursor-pointer"
      />
    </div>
  );
};

const TableRow = ({
  cls,
  onClassChange,
  influencerId,
}: {
  cls: AugmentedClass;
  onClassChange: () => void;
  influencerId;
}) => {
  const editModal = useModal(EditForm);
  const duplicateModal = useModal(EditForm);

  const [updateClass] = useUpdateLiveClassMutation();
  const [addClass] = useAddLiveClassMutation();

  return (
    <>
      <div className="grid grid-cols-5 gap-x-4 py-[6px] px-[10px] text-[#171E33] border-b border-b-[#F7F7FB]">
        <div className="flex items-center space-x-4">
          <div
            className="rounded-full w-[40px] h-[40px] bg-[#e3e6ed] bg-cover bg-center"
            style={{
              backgroundImage: `url('${cls.cover.cdn01 + cls.cover.image}')`,
            }}
          />
          <div>{cls.name}</div>
        </div>
        <div className="flex items-center">
          {isSameYear(cls.dtstart, Date.now())
            ? formatInTimeZone(cls.dtstart, 'America/New_York', 'MMM d')
            : formatInTimeZone(cls.dtstart, 'America/New_York', 'MMM d, yyyy')}
        </div>
        <div className="flex items-center">
          {formatInTimeZone(cls.dtstart, 'America/New_York', 'hh:mm aa')}
        </div>
        <div className="flex items-center">
          {cls.duration != null ? cls.duration + ' min' : '-'}
        </div>
        <div className="flex items-center justify-end pr-6 space-x-6">
          {cls.isUpcoming ? (
            <FontAwesomeIcon
              icon={faPen as any}
              className="text-primary text-md leading-none cursor-pointer"
              onClick={() => editModal.open()}
            />
          ) : null}
          {cls.isUpcoming && cls.deepLink ? (
            <CopyDeepLink {...{ cls }} />
          ) : null}
          <FontAwesomeIcon
            icon={faClone as any}
            className="text-primary text-md leading-none cursor-pointer"
            onClick={() => duplicateModal.open()}
          />
        </div>
      </div>
      <editModal.Popup
        initialData={clsToFormData(cls as any)}
        isEdit={true}
        influencerId={influencerId}
        onSubmit={(data) =>
          updateClass({
            variables: {
              data: {
                id: cls.id,
                ...formDataToMutationData(influencerId, data),
              },
            },
          }).then(() => onClassChange())
        }
      />
      <duplicateModal.Popup
        initialData={clsToFormData(cls as any, true)}
        isEdit={false}
        influencerId={influencerId}
        onSubmit={(data) =>
          addClass({
            variables: {
              data: {
                ...formDataToMutationData(influencerId, data),
              },
            },
          }).then(() => onClassChange())
        }
      />
    </>
  );
};

export const Table = ({
  classes,
  onClassChange,
  influencerId,
}: {
  classes: AugmentedClass[];
  onClassChange: () => void;
  influencerId;
}) => {
  const headers = (
    <div className="grid grid-cols-5 gap-x-4 bg-[#F3F3F3] text-[#848994] font-semibold pt-[11px] px-[10px] pb-[11px]">
      <div>Name</div>
      <div>Date</div>
      <div>Time (EST)</div>
      <div>Duration</div>
      <div>{/* Toolbar */}</div>
    </div>
  );

  return (
    <div className="border border-[#e3e6ed] rounded-[8px]">
      {headers}
      {classes.map((x, i) => (
        <TableRow
          onClassChange={onClassChange}
          key={i}
          cls={x}
          {...{ influencerId }}
        />
      ))}
    </div>
  );
};
